import React, { useEffect, useRef, useState } from 'react';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';

const values = [
  {
    icon: '⚡',
    title: 'La Réactivité',
    desc: 'Nous intervenons rapidement pour assister les conducteurs en difficulté.',
    color: '#FF6B00',
    bg: 'rgba(255,107,0,0.08)',
  },
  {
    icon: '🎯',
    title: 'Le Professionnalisme',
    desc: 'Chaque intervention est réalisée avec sérieux, compétence et respect.',
    color: '#3B82F6',
    bg: 'rgba(59,130,246,0.08)',
  },
  {
    icon: '🛡️',
    title: 'La Fiabilité',
    desc: 'Nos clients peuvent compter sur un service disponible et efficace.',
    color: '#10B981',
    bg: 'rgba(16,185,129,0.08)',
  },
  {
    icon: '🔒',
    title: 'La Sécurité',
    desc: "Nous plaçons la sécurité des conducteurs au cœur de nos actions.",
    color: '#8B5CF6',
    bg: 'rgba(139,92,246,0.08)',
  },
  {
    icon: '🤝',
    title: "L'Engagement",
    desc: "Nous nous engageons à offrir un service de qualité pour répondre aux besoins des automobilistes.",
    color: '#F59E0B',
    bg: 'rgba(245,158,11,0.08)',
  },
];

function CountUp({ target, suffix = '' }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });

  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const step = target / 60;
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [inView, target]);

  return <span ref={ref}>{count}{suffix}</span>;
}

function AnimBlock({ children, delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.div ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

export default function AboutPage() {
  const { scrollYProgress } = useScroll();
  const heroY = useTransform(scrollYProgress, [0, 0.3], [0, -80]);

  return (
    <div style={{ minHeight: '100vh', background: '#060D18', color: 'white', overflowX: 'hidden' }}>

      {/* ── HERO ── */}
      <section style={{ position: 'relative', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
        {/* Animated background */}
        <div style={{ position: 'absolute', inset: 0 }}>
          <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse 80% 60% at 50% 40%, rgba(255,107,0,0.18) 0%, transparent 70%)' }} />
          <motion.div animate={{ scale: [1, 1.08, 1], opacity: [0.3, 0.5, 0.3] }} transition={{ duration: 8, repeat: Infinity }}
            style={{ position: 'absolute', top: '20%', left: '10%', width: 400, height: 400, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,107,0,0.12), transparent)', filter: 'blur(60px)' }} />
          <motion.div animate={{ scale: [1, 1.1, 1], opacity: [0.2, 0.4, 0.2] }} transition={{ duration: 10, repeat: Infinity, delay: 3 }}
            style={{ position: 'absolute', bottom: '15%', right: '5%', width: 500, height: 500, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,107,0,0.08), transparent)', filter: 'blur(80px)' }} />
          {/* Grid lines */}
          <div style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(255,107,0,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,107,0,0.04) 1px, transparent 1px)', backgroundSize: '60px 60px', opacity: 0.5 }} />
        </div>

        <motion.div style={{ y: heroY, position: 'relative', zIndex: 1, textAlign: 'center', padding: '8rem 1.5rem 4rem' }} className="container">
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span style={{ display: 'inline-block', background: 'rgba(255,107,0,0.15)', border: '1px solid rgba(255,107,0,0.3)', borderRadius: '100px', padding: '0.4rem 1.2rem', fontSize: '0.75rem', letterSpacing: '3px', textTransform: 'uppercase', color: 'var(--orange)', marginBottom: '2rem' }}>
              Notre histoire
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }}
            style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(3rem, 9vw, 7rem)', lineHeight: 0.95, letterSpacing: '-1px', marginBottom: '2rem' }}
          >
            QUI SOMMES<br />
            <span style={{ background: 'linear-gradient(90deg, #FF6B00, #FF9A3C, #FFB86B)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>NOUS ?</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.4 }}
            style={{ maxWidth: 620, margin: '0 auto 3rem', fontSize: 'clamp(1rem, 2vw, 1.2rem)', color: 'rgba(255,255,255,0.55)', lineHeight: 1.8 }}
          >
            ELIM DEPANNAGE est un service de dépannage automobile dédié à l'assistance rapide des conducteurs en difficulté sur la route.
          </motion.p>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }}
            style={{ display: 'flex', justifyContent: 'center', gap: '2rem', flexWrap: 'wrap' }}>
            {[
              { val: 450, suffix: '+', label: 'Prestataires' },
              { val: 24, suffix: 'h/24', label: 'Disponibilité' },
              { val: 3, suffix: ' villes', label: 'Au Bénin' },
            ].map((s, i) => (
              <motion.div key={i} initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.9 + i * 0.1 }}
                style={{ textAlign: 'center', padding: '1.2rem 2rem', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '16px', backdropFilter: 'blur(10px)' }}>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: '2.5rem', color: 'var(--orange)', lineHeight: 1 }}>
                  <CountUp target={s.val} suffix={s.suffix} />
                </div>
                <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.4)', marginTop: '0.3rem', letterSpacing: '1px', textTransform: 'uppercase' }}>{s.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div animate={{ y: [0, 10, 0] }} transition={{ duration: 2, repeat: Infinity }}
          style={{ position: 'absolute', bottom: '2rem', left: '50%', transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem', opacity: 0.4 }}>
          <div style={{ width: 1, height: 40, background: 'linear-gradient(to bottom, transparent, var(--orange))' }} />
          <span style={{ fontSize: '0.65rem', letterSpacing: '2px', textTransform: 'uppercase' }}>Défiler</span>
        </motion.div>
      </section>

      {/* ── STORY ── */}
      <section style={{ padding: '7rem 0', background: '#080F1A' }}>
        <div className="container">
          <AnimBlock>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5rem', alignItems: 'center' }} className="about-grid">
              <div>
                <span style={{ display: 'block', fontSize: '0.7rem', letterSpacing: '4px', color: 'var(--orange)', textTransform: 'uppercase', marginBottom: '1rem' }}>— Notre histoire —</span>
                <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(2rem, 4vw, 3.2rem)', lineHeight: 1.05, marginBottom: '1.5rem', color: 'white' }}>
                  NÉ D'UN<br /><span style={{ color: 'var(--orange)' }}>CONSTAT SIMPLE</span>
                </h2>
                <p style={{ color: 'rgba(255,255,255,0.55)', lineHeight: 1.9, fontSize: '0.95rem', marginBottom: '1.2rem' }}>
                  Dans la ville de Cotonou, de nombreuses pannes de véhicules surviennent en pleine circulation. Ces situations provoquent souvent d'importants embouteillages, perturbent la fluidité du trafic et placent les conducteurs dans des situations stressantes et parfois dangereuses.
                </p>
                <p style={{ color: 'rgba(255,255,255,0.55)', lineHeight: 1.9, fontSize: '0.95rem', marginBottom: '2rem' }}>
                  Face à cette réalité, <strong style={{ color: 'white' }}>ELIM DEPANNAGE</strong> a été créé pour apporter une solution rapide, efficace et professionnelle aux automobilistes confrontés à une panne.
                </p>
                <div style={{ padding: '1.2rem 1.5rem', background: 'rgba(255,107,0,0.08)', border: '1px solid rgba(255,107,0,0.2)', borderRadius: '12px', borderLeft: '4px solid var(--orange)' }}>
                  <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem', lineHeight: 1.7, fontStyle: 'italic', margin: 0 }}>
                    "Grâce à notre engagement et à notre disponibilité, nous proposons un service de dépannage fiable, accessible et disponible <strong style={{ color: 'var(--orange)' }}>24h/24</strong> pour accompagner les conducteurs en cas de panne."
                  </p>
                </div>
              </div>

              <div style={{ position: 'relative' }}>
                <div style={{ position: 'relative', borderRadius: '20px', overflow: 'hidden' }}>
                  <img src="/images/mec.jpg" alt="Mécanicien ELIM" style={{ width: '100%', height: 420, objectFit: 'cover', borderRadius: '20px', filter: 'brightness(0.8)' }}
                    onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                  <div style={{ display: 'none', width: '100%', height: 420, borderRadius: '20px', background: 'linear-gradient(135deg, #1A3B5D, #0D1B2A)', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '1rem' }}>
                    <span style={{ fontSize: '5rem' }}>🔧</span>
                    <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.9rem' }}>ELIM DEPANNAGE</span>
                  </div>
                  <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(8,15,26,0.8) 0%, transparent 50%)', borderRadius: '20px' }} />
                </div>
                {/* Floating badge */}
                <motion.div animate={{ y: [-5, 5, -5] }} transition={{ duration: 4, repeat: Infinity }}
                  style={{ position: 'absolute', bottom: -20, left: -20, background: 'var(--orange)', borderRadius: '16px', padding: '1rem 1.5rem', boxShadow: '0 20px 40px rgba(255,107,0,0.4)' }}>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.8rem', lineHeight: 1 }}>24h</div>
                  <div style={{ fontSize: '0.7rem', opacity: 0.8, letterSpacing: '1px' }}>DISPONIBLE</div>
                </motion.div>
                <motion.div animate={{ y: [5, -5, 5] }} transition={{ duration: 5, repeat: Infinity }}
                  style={{ position: 'absolute', top: -15, right: -15, background: '#10B981', borderRadius: '12px', padding: '0.8rem 1.2rem', boxShadow: '0 12px 30px rgba(16,185,129,0.3)' }}>
                  <div style={{ fontSize: '0.7rem', letterSpacing: '1px', fontWeight: 700 }}>✓ VÉRIFIÉ</div>
                </motion.div>
              </div>
            </div>
          </AnimBlock>
        </div>
      </section>

      {/* ── MISSION & VISION ── */}
      <section style={{ padding: '7rem 0', background: '#060D18', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(255,107,0,0.06) 0%, transparent 50%), radial-gradient(circle at 80% 50%, rgba(59,130,246,0.06) 0%, transparent 50%)' }} />
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <AnimBlock>
            <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
              <span style={{ fontSize: '0.7rem', letterSpacing: '4px', color: 'var(--orange)', textTransform: 'uppercase' }}>— Ce qui nous guide —</span>
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(2rem, 5vw, 3.5rem)', marginTop: '0.8rem' }}>MISSION & VISION</h2>
            </div>
          </AnimBlock>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }} className="mv-grid">
            {[
              {
                icon: '🎯',
                title: 'Notre Mission',
                color: '#FF6B00',
                text: "Fournir une assistance rapide et efficace aux automobilistes en panne, afin de garantir leur sécurité et de faciliter la fluidité de la circulation. Nous nous engageons à intervenir dans les meilleurs délais pour dépanner, sécuriser ou remorquer les véhicules immobilisés sur la route.",
              },
              {
                icon: '🌍',
                title: 'Notre Vision',
                color: '#3B82F6',
                text: "Devenir une référence du dépannage automobile à Cotonou et dans tout le Bénin, en offrant un service reconnu pour sa rapidité, sa fiabilité et son professionnalisme. Nous souhaitons contribuer à une circulation plus fluide et plus sûre en réduisant l'impact des pannes de véhicules sur la route.",
              },
            ].map((item, i) => (
              <AnimBlock key={i} delay={i * 0.15}>
                <div style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${item.color}20`, borderRadius: '24px', padding: '3rem', height: '100%', position: 'relative', overflow: 'hidden' }}>
                  <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, ${item.color}, transparent)` }} />
                  <div style={{ fontSize: '3rem', marginBottom: '1.5rem' }}>{item.icon}</div>
                  <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.8rem', color: item.color, marginBottom: '1.2rem' }}>{item.title}</h3>
                  <p style={{ color: 'rgba(255,255,255,0.55)', lineHeight: 1.9, fontSize: '0.95rem' }}>{item.text}</p>
                </div>
              </AnimBlock>
            ))}
          </div>
        </div>
      </section>

      {/* ── VALUES ── */}
      <section style={{ padding: '7rem 0', background: '#080F1A' }}>
        <div className="container">
          <AnimBlock>
            <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
              <span style={{ fontSize: '0.7rem', letterSpacing: '4px', color: 'var(--orange)', textTransform: 'uppercase' }}>— Ce en quoi nous croyons —</span>
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(2rem, 5vw, 3.5rem)', marginTop: '0.8rem' }}>NOS VALEURS</h2>
            </div>
          </AnimBlock>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1.5rem' }}>
            {values.map((v, i) => (
              <AnimBlock key={i} delay={i * 0.1}>
                <motion.div whileHover={{ y: -8, scale: 1.02 }} transition={{ type: 'spring', stiffness: 300 }}
                  style={{ background: v.bg, border: `1px solid ${v.color}25`, borderRadius: '20px', padding: '2rem', cursor: 'default', position: 'relative', overflow: 'hidden' }}>
                  <div style={{ position: 'absolute', top: 0, right: 0, width: 80, height: 80, background: `radial-gradient(circle, ${v.color}15, transparent)`, borderRadius: '0 20px 0 80px' }} />
                  <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>{v.icon}</div>
                  <h3 style={{ fontFamily: 'var(--font-cond)', fontSize: '1.1rem', color: v.color, marginBottom: '0.8rem', textTransform: 'uppercase', letterSpacing: '1px' }}>{v.title}</h3>
                  <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.88rem', lineHeight: 1.7 }}>{v.desc}</p>
                </motion.div>
              </AnimBlock>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section style={{ padding: '6rem 0', background: 'linear-gradient(135deg, #FF6B00 0%, #FF4500 100%)', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%)' }} />
        <div className="container" style={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
          <AnimBlock>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(2rem, 5vw, 3.5rem)', marginBottom: '1rem', color: 'white' }}>
              REJOIGNEZ ELIM DEPANNAGE
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '1.1rem', marginBottom: '2.5rem', maxWidth: 500, margin: '0 auto 2.5rem' }}>
              Que vous soyez en panne ou prestataire, nous sommes là pour vous.
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/inscription" style={{ background: 'white', color: 'var(--orange)', padding: '0.9rem 2rem', borderRadius: '8px', fontWeight: 700, fontFamily: 'var(--font-cond)', fontSize: '1rem', letterSpacing: '1px', textDecoration: 'none', transition: 'all 0.2s' }}>
                S'INSCRIRE GRATUITEMENT
              </Link>
              <Link to="/contact" style={{ background: 'rgba(255,255,255,0.15)', color: 'white', padding: '0.9rem 2rem', borderRadius: '8px', fontWeight: 700, fontFamily: 'var(--font-cond)', fontSize: '1rem', letterSpacing: '1px', textDecoration: 'none', border: '1px solid rgba(255,255,255,0.3)' }}>
                NOUS CONTACTER
              </Link>
            </div>
          </AnimBlock>
        </div>
      </section>

      <style>{`
        @media (max-width: 768px) {
          .about-grid { grid-template-columns: 1fr !important; gap: 3rem !important; }
          .mv-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}
